import isObject from 'lodash/isObject';
import styles from '@features/homepage/sections/Voxbox/VoxboxSection.styles';
import { IVoxboxContent } from '@features/cms/typescript';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import { ICmsLink, IVideoType, TImage } from '@components/typescript';
import Button from '@components/Button/Button';
import VideoPlayer from '@components/Video/VideoPlayer';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import useIntersect from '@hooks/useIntersect';
import PlayIcon from '@components/icons/PlayIcon';
type PropsType = {
  thumbnail: TImage;
  blur: TImage;
  link: ICmsLink | string;
  buttonText: string;
  content: IVoxboxContent;
  video: IVideoType | null;
  isFirst: boolean;
};
const VoxboxSectionContent = ({
  thumbnail,
  link,
  blur,
  content,
  buttonText,
  video,
  isFirst
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    description,
    title,
    titleColor,
    descriptionColor,
    topText,
    topTextColor
  } = content;
  const [playVideo, setPlayVideo] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const isPausedByUserRef = useRef(false);
  const entry = useIntersect(videoContainerRef, {
    rootMargin: '0px',
    threshold: 0.5
  });
  useEffect(() => {
    if (!video || entry === undefined) {
      return;
    }
    if (entry.isIntersecting) {
      if (!isPausedByUserRef.current) {
        setPlayVideo(true);
      }
      return;
    }
    setPlayVideo(false);
    isPausedByUserRef.current = false;
  }, [entry, video]);
  const handleVideoToggle = () => {
    if (!video) {
      return;
    }
    setPlayVideo(prev => {
      const next = !prev;
      isPausedByUserRef.current = !next;
      return next;
    });
  };
  return <>
      <div sx={styles.bgContainer(!!video)} ref={videoContainerRef} role={video ? 'button' : undefined} tabIndex={video ? 0 : undefined} aria-label={video ? t('productVideo') : undefined} data-testid="VoxboxSectionContent-bgContainer">
        {video ? <>
            {!playVideo && <div sx={styles.playVideoOverlay}>
                <PlayIcon onClick={handleVideoToggle} />
              </div>}
            <VideoPlayer video={video} playVideo={playVideo} poster={{
          image: thumbnail,
          blur
        }} loop onClick={handleVideoToggle} soundToggler />
          </> : <ResponsiveImage image={thumbnail} blur={blur} alt="" />}
      </div>
      <div sx={styles.textWrapper} data-cy="VoxboxContent">
        {!!topText && <p sx={styles.sliderTopText(topTextColor)}>{topText}</p>}
        <Heading h={isFirst ? 1 : 2} display={3} mobile color={titleColor || theme.rawColors.neutralWhite} pb={16} align="center">
          {title}
        </Heading>
        <Heading h={4} headingType={4} mobile color={descriptionColor || theme.rawColors.neutralWhite} align="center">
          {description}
        </Heading>
        {buttonText && link && <div sx={styles.buttonLinkPosition}>
            <Button variant="outlined" variantColor="white" size="default" href={isObject(link) ? prepareURLByCMSLink(link) : link as string}>
              {buttonText}
            </Button>
          </div>}
      </div>
    </>;
};
export default VoxboxSectionContent;